import { type Static, Type } from "@sinclair/typebox"
import { AuthoredCardSchema } from "./cards"
import { IdSchema, IsoTimestampSchema } from "./common"
import { DeckSchema } from "./decks"
import { HeroDefinitionSchema } from "./heroes"

export const ManagedCardRecordSchema = Type.Object({
  card: AuthoredCardSchema,
  ownerUserId: Type.Optional(IdSchema),
  remoteSyncConflictAt: Type.Optional(IsoTimestampSchema),
  remoteSyncFailedAt: Type.Optional(IsoTimestampSchema),
  remoteSyncedAt: Type.Optional(IsoTimestampSchema),
  updatedAt: IsoTimestampSchema,
  deckUsageCount: Type.Integer({
    minimum: 0,
  }),
})

export type ManagedCardRecord = Static<typeof ManagedCardRecordSchema>

export const ManagedCardSyncEnvelopeSchema = Type.Object({
  record: ManagedCardRecordSchema,
})

export type ManagedCardSyncEnvelope = Static<
  typeof ManagedCardSyncEnvelopeSchema
>

export const ManagedCardSyncResponseSchema = ManagedCardSyncEnvelopeSchema
export type ManagedCardSyncResponse = Static<
  typeof ManagedCardSyncResponseSchema
>

export const CardLibraryStoreSchema = Type.Array(ManagedCardRecordSchema, {
  maxItems: 2000,
})

export type CardLibraryStore = Static<typeof CardLibraryStoreSchema>

export const CardLibraryListResponseSchema = Type.Object({
  records: CardLibraryStoreSchema,
})

export type CardLibraryListResponse = Static<
  typeof CardLibraryListResponseSchema
>

export const BulkCardSyncEnvelopeSchema = Type.Object({
  records: Type.Array(ManagedCardRecordSchema, { maxItems: 200 }),
})

export type BulkCardSyncEnvelope = Static<typeof BulkCardSyncEnvelopeSchema>

const BulkCardSyncResultSchema = Type.Union([
  Type.Object({
    status: Type.Literal("synced"),
    record: ManagedCardRecordSchema,
  }),
  Type.Object({ status: Type.Literal("conflict") }),
  Type.Object({ status: Type.Literal("forbidden") }),
])

export const BulkCardSyncResponseSchema = Type.Object({
  results: Type.Array(BulkCardSyncResultSchema),
})

export type BulkCardSyncResponse = Static<typeof BulkCardSyncResponseSchema>

export const ManagedHeroRecordSchema = Type.Object({
  hero: HeroDefinitionSchema,
  ownerUserId: Type.Optional(IdSchema),
  remoteSyncConflictAt: Type.Optional(IsoTimestampSchema),
  remoteSyncFailedAt: Type.Optional(IsoTimestampSchema),
  remoteSyncedAt: Type.Optional(IsoTimestampSchema),
  updatedAt: IsoTimestampSchema,
})

export type ManagedHeroRecord = Static<typeof ManagedHeroRecordSchema>

export const ManagedHeroSyncEnvelopeSchema = Type.Object({
  record: ManagedHeroRecordSchema,
})

export type ManagedHeroSyncEnvelope = Static<
  typeof ManagedHeroSyncEnvelopeSchema
>

export const ManagedHeroSyncResponseSchema = ManagedHeroSyncEnvelopeSchema
export type ManagedHeroSyncResponse = Static<
  typeof ManagedHeroSyncResponseSchema
>

export const HeroLibraryStoreSchema = Type.Array(ManagedHeroRecordSchema, {
  maxItems: 200,
})

export type HeroLibraryStore = Static<typeof HeroLibraryStoreSchema>

export const HeroLibraryListResponseSchema = Type.Object({
  records: HeroLibraryStoreSchema,
})

export type HeroLibraryListResponse = Static<
  typeof HeroLibraryListResponseSchema
>

export const BulkHeroSyncEnvelopeSchema = Type.Object({
  records: Type.Array(ManagedHeroRecordSchema, { maxItems: 50 }),
})

export type BulkHeroSyncEnvelope = Static<typeof BulkHeroSyncEnvelopeSchema>

const BulkHeroSyncResultSchema = Type.Union([
  Type.Object({
    status: Type.Literal("synced"),
    record: ManagedHeroRecordSchema,
  }),
  Type.Object({ status: Type.Literal("conflict") }),
  Type.Object({ status: Type.Literal("forbidden") }),
])

export const BulkHeroSyncResponseSchema = Type.Object({
  results: Type.Array(BulkHeroSyncResultSchema),
})

export type BulkHeroSyncResponse = Static<typeof BulkHeroSyncResponseSchema>

export const ManagedDeckRecordSchema = Type.Object({
  deck: DeckSchema,
  ownerUserId: Type.Optional(IdSchema),
  remoteSyncConflictAt: Type.Optional(IsoTimestampSchema),
  remoteSyncFailedAt: Type.Optional(IsoTimestampSchema),
  remoteSyncedAt: Type.Optional(IsoTimestampSchema),
  updatedAt: IsoTimestampSchema,
})

export type ManagedDeckRecord = Static<typeof ManagedDeckRecordSchema>

export const ManagedDeckSyncEnvelopeSchema = Type.Object({
  record: ManagedDeckRecordSchema,
})

export type ManagedDeckSyncEnvelope = Static<
  typeof ManagedDeckSyncEnvelopeSchema
>

export const ManagedDeckSyncResponseSchema = ManagedDeckSyncEnvelopeSchema
export type ManagedDeckSyncResponse = Static<
  typeof ManagedDeckSyncResponseSchema
>

export const DeckLibraryStoreSchema = Type.Array(ManagedDeckRecordSchema, {
  maxItems: 99,
})

export type DeckLibraryStore = Static<typeof DeckLibraryStoreSchema>

export const DeckLibraryListResponseSchema = Type.Object({
  records: DeckLibraryStoreSchema,
})

export type DeckLibraryListResponse = Static<
  typeof DeckLibraryListResponseSchema
>

export const BulkDeckSyncEnvelopeSchema = Type.Object({
  records: Type.Array(ManagedDeckRecordSchema, { maxItems: 50 }),
})

export type BulkDeckSyncEnvelope = Static<typeof BulkDeckSyncEnvelopeSchema>

const BulkDeckSyncResultSchema = Type.Union([
  Type.Object({
    status: Type.Literal("synced"),
    record: ManagedDeckRecordSchema,
  }),
  Type.Object({ status: Type.Literal("conflict") }),
  Type.Object({ status: Type.Literal("forbidden") }),
])

export const BulkDeckSyncResponseSchema = Type.Object({
  results: Type.Array(BulkDeckSyncResultSchema),
})

export type BulkDeckSyncResponse = Static<typeof BulkDeckSyncResponseSchema>

export const BulkDeleteEnvelopeSchema = Type.Object({
  ids: Type.Array(Type.String(), { maxItems: 500 }),
})

export type BulkDeleteEnvelope = Static<typeof BulkDeleteEnvelopeSchema>
